import { DateTime } from 'luxon'
import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BooleanParam, JsonParam, NumberParam, StringParam, UrlUpdateType, useQueryParams } from 'use-query-params'

import { Nullable } from '@types'

import alertsApi from '@modules/alerts/api'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { parseJSONSafely, removeObjProperty } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'

import { DEFAULT_SORT_OPTION, getDefaultSearchableProfilesState } from '../constants'
import {
  FiltersSetup,
  HistoryFilterOptions,
  MetaFilterSelectedData,
  MetaFilterSelectedDate,
  MetaFilterTypes,
  ProfileType,
  SelectedFiltersMapByProfiles,
  SelectedMetaFilters,
} from '../types'

export const useSearchQueryParams = () => {
  const [query, setQuery] = useQueryParams(
    {
      sort_by: NumberParam,
      group_by: StringParam,
      page: NumberParam,
      query: StringParam,
      filters: JsonParam,
      expanded_filter: JsonParam,
      profiles: JsonParam,
      all_profiles: BooleanParam,
      meta_filter: JsonParam,
      saved_search_name: StringParam,
      saved_search_id: NumberParam,
      search_by_history: NumberParam,
      eventsFilter: JsonParam,
      scrollX: NumberParam,
      scrollY: NumberParam,
    },
    {
      removeDefaultsFromUrl: true,
      enableBatching: true,
    },
  )

  const setQuerySafe = (changes: object, updateType?: UrlUpdateType) => {
    if (window.location.href.includes('search')) {
      setQuery(changes, updateType)
    }
  }

  const previousQueryRef = useRef(query)
  const navigate = useNavigate()

  const { hasAccessToCompanyMeetings } = useFeatureFlags()

  // State and Setters
  const [selectedSortId, setSelectedSortId] = useState(query.sort_by || DEFAULT_SORT_OPTION.id)
  const [selectedGroupingId, setSelectedGroupingId] = useState(query.group_by ? Number(query.group_by) : 0)
  const [page, setPage] = useState(query.page || 1)
  const [searchString, setSearchString] = useState(query.query || '')
  const [selectedFiltersByProfiles, setSelectedFiltersByProfiles] = useState(query.filters || {})
  const [expandedFilter, setExpandedFilter] = useState(query.expanded_filter || {})
  const [searchableProfilesMap, setSearchableProfilesMap] = useState(query.profiles || getDefaultSearchableProfilesState(false))
  const [isSearchAllProfilesChecked, setSearchAllProfilesChecked] = useState<boolean>(query.all_profiles ?? true)
  const [selectedMetaFilter, setSelectedMetaFilter] = useState(query.meta_filter || { type: MetaFilterTypes.DEFAULT, value: '' })
  const [savedSearchName, setSavedSearchName] = useState(query.saved_search_name || '')
  const [savedSearchId, setSavedSearchId] = useState<Nullable<number>>(query.saved_search_id || null)
  const [searchHistoryTypeId, setSearchHistoryTypeId] = useState<number | undefined>(query.search_by_history || 0)
  const [eventsFilterData, setEventsFilter] = useState<Nullable<{ includeIds: number[]; excludeIds: number[] }>>(
    query.eventsFilter || null,
  )
  const scrollX = query.scrollX || 0
  const scrollY = query.scrollY || 0

  const setScrollX = (value: number) => {
    setQuerySafe({ scrollX: value }, 'pushIn')
  }

  const setScrollY = (value: number) => {
    setQuerySafe({ scrollY: value }, 'pushIn')
  }

  const propertyUpdateMethodGetter = (property: unknown | undefined) => (property === undefined ? 'replaceIn' : 'pushIn')

  useEffect(() => {
    if (!savedSearchName) {
      setSavedSearchId(null)
    }
  }, [savedSearchName])

  // Update the URL when states change
  useEffect(() => {
    setQuerySafe({ sort_by: selectedSortId }, propertyUpdateMethodGetter(previousQueryRef.current.sort_by))
  }, [selectedSortId])

  useEffect(() => {
    setQuerySafe({ group_by: String(selectedGroupingId) }, propertyUpdateMethodGetter(previousQueryRef.current.group_by))
  }, [selectedGroupingId])

  useEffect(() => {
    setQuerySafe({ page }, propertyUpdateMethodGetter(previousQueryRef.current.page))
  }, [page])

  useEffect(() => {
    setQuerySafe({ query: searchString }, propertyUpdateMethodGetter(previousQueryRef.current.query))
  }, [searchString])

  useEffect(() => {
    setQuerySafe({ filters: selectedFiltersByProfiles }, propertyUpdateMethodGetter(previousQueryRef.current.filters))
  }, [selectedFiltersByProfiles])

  useEffect(() => {
    setQuerySafe({ expanded_filter: expandedFilter }, propertyUpdateMethodGetter(previousQueryRef.current.expanded_filter))
  }, [expandedFilter])

  useEffect(() => {
    setQuerySafe({ profiles: searchableProfilesMap }, propertyUpdateMethodGetter(previousQueryRef.current.profiles))
  }, [searchableProfilesMap])

  useEffect(() => {
    setQuerySafe({ all_profiles: isSearchAllProfilesChecked }, propertyUpdateMethodGetter(previousQueryRef.current.all_profiles))
  }, [isSearchAllProfilesChecked])

  useEffect(() => {
    setQuerySafe({ meta_filter: selectedMetaFilter }, propertyUpdateMethodGetter(previousQueryRef.current.meta_filter))
  }, [selectedMetaFilter])

  useEffect(() => {
    const method =
      previousQueryRef.current.saved_search_id === undefined || previousQueryRef.current.saved_search_id === null
        ? 'replaceIn'
        : 'pushIn'
    setQuerySafe({ saved_search_id: savedSearchId }, method)
  }, [savedSearchId])

  useEffect(() => {
    setQuerySafe({ saved_search_name: savedSearchName }, propertyUpdateMethodGetter(previousQueryRef.current.saved_search_name))
  }, [savedSearchName])

  useEffect(() => {
    const method =
      (previousQueryRef.current.search_by_history === undefined || previousQueryRef.current.search_by_history === null) &&
      (searchHistoryTypeId === null || searchHistoryTypeId === undefined)
        ? 'replaceIn'
        : 'pushIn'

    setQuerySafe({ search_by_history: searchHistoryTypeId }, method)
  }, [searchHistoryTypeId])

  useEffect(() => {
    const method =
      (previousQueryRef.current.eventsFilter === undefined || previousQueryRef.current.eventsFilter === null) &&
      (eventsFilterData === null || eventsFilterData === undefined)
        ? 'replaceIn'
        : 'pushIn'
    setQuerySafe({ eventsFilter: eventsFilterData }, method)
  }, [eventsFilterData])

  useEffect(() => {
    setQuerySafe({ scrollX }, propertyUpdateMethodGetter(query.scrollX))
  }, [scrollX])

  useEffect(() => {
    setQuerySafe({ scrollY }, propertyUpdateMethodGetter(query.scrollY))
  }, [scrollY])

  const [filtersSetup, setFiltersSetup] = useState(parseJSONSafely(sessionStorage.getItem('filtersSettings') ?? '') || null)
  const [metaFilters, setMetaFilters] = useState({})

  const filtersSetupRef = useRef(filtersSetup)

  useEffect(() => {
    filtersSetupRef.current = filtersSetup
  }, [filtersSetup])

  useEffect(() => {
    sessionStorage.setItem('filtersSettings', JSON.stringify(filtersSetupRef.current || ''))
  }, [filtersSetup])

  const clearToDefault = () => {
    setSelectedSortId(DEFAULT_SORT_OPTION.id)
    setSelectedGroupingId(0)
    setPage(1)
    setSearchString('')
    setSelectedFiltersByProfiles({})
    setExpandedFilter({})
    setSearchableProfilesMap(getDefaultSearchableProfilesState(false))
    setSearchAllProfilesChecked(true)
    setSelectedMetaFilter({ type: MetaFilterTypes.DEFAULT, value: '' })
    setFiltersSetup(null)
    setMetaFilters({})
    setSearchHistoryTypeId(undefined)
    setEventsFilter(null)
    setScrollX(0)
    setScrollY(0)

    setSavedSearchName('')
    // Reflect the default state in the URL
    setQuerySafe({}, 'replace') // Using 'replace' to replace current state in history
  }

  // update state when url changes without refreshing
  useEffect(() => {
    if (!hasAccessToCompanyMeetings && query.search_by_history === HistoryFilterOptions.COMPANY_MEETINGS) {
      navigate('/plus/hub')
      return
    }
    if (query.meta_filter) {
      const isNewItemsFilter = query.meta_filter.type === MetaFilterTypes.BY_NEW_ITEMS
      setSelectedMetaFilter({ ...query.meta_filter, filterByNewItems: isNewItemsFilter })
    }

    // Only proceed if the current query is different from the previous query
    if (JSON.stringify(previousQueryRef.current) !== JSON.stringify(query)) {
      if (query.sort_by !== selectedSortId) setSelectedSortId(query.sort_by || DEFAULT_SORT_OPTION.id)
      if (Number(query.group_by) !== selectedGroupingId) setSelectedGroupingId(query.group_by ? Number(query.group_by) : 0)
      if (query.page !== page) setPage(query.page || 1)
      if (query.query !== searchString) setSearchString(query.query || '')
      if (JSON.stringify(query.filters) !== JSON.stringify(selectedFiltersByProfiles))
        setSelectedFiltersByProfiles(query.filters || {})
      if (JSON.stringify(query.expanded_filter) !== JSON.stringify(expandedFilter)) setExpandedFilter(query.expanded_filter || {})
      if (JSON.stringify(query.profiles) !== JSON.stringify(searchableProfilesMap))
        setSearchableProfilesMap(query.profiles || getDefaultSearchableProfilesState(false))
      if (query.all_profiles !== isSearchAllProfilesChecked) setSearchAllProfilesChecked(query.all_profiles ?? true)
      if (JSON.stringify(query.meta_filter) !== JSON.stringify(selectedMetaFilter)) {
        setSelectedMetaFilter(query.meta_filter || { type: MetaFilterTypes.DEFAULT, value: '' })
      }
      if (query.search_by_history !== searchHistoryTypeId) {
        if (hasAccessToCompanyMeetings || query.search_by_history !== HistoryFilterOptions.COMPANY_MEETINGS) {
          setSearchHistoryTypeId(Number(query.search_by_history))
        }
      }
      if (JSON.stringify(query.eventsFilter) !== JSON.stringify(eventsFilterData)) {
        setEventsFilter(query.eventsFilter)
      }

      // Update the previous query ref value
      previousQueryRef.current = query
    }
  }, [query])

  return {
    savedSearchId,
    setSavedSearchId,
    setAppliedSavedSearchName: setSavedSearchName,
    appliedSavedSearchName: savedSearchName,
    selectedSortId,
    setSelectedSortId,
    selectedGroupingId,
    setSelectedGroupingId,
    page,
    scrollX,
    scrollY,
    setPage,
    setScrollX,
    setScrollY,
    searchString,
    setSearchString,
    selectedFiltersByProfiles,
    setSelectedFiltersByProfiles,
    expandedFilter,
    setExpandedFilter,
    searchableProfilesMap,
    setSearchableProfilesMap,
    isSearchAllProfilesChecked,
    setSearchAllProfilesChecked,
    selectedMetaFilter,
    setSelectedMetaFilter,
    filtersSetup,
    setFiltersSetup,
    metaFilters,
    setMetaFilters,
    eventsFilterData,
    setEventsFilter: (data: Nullable<{ includeIds: number[]; excludeIds: number[] }>) => {
      setPage(1)
      setEventsFilter(data)
      setSavedSearchName('')
      setScrollX(0)
      setScrollY(0)
    },
    clear: clearToDefault,
    setSearchHistoryTypeId: (id: number | undefined) => {
      setPage(1)
      setSavedSearchName('')
      setSearchHistoryTypeId(id)
      setScrollX(0)
      setScrollY(0)
    },
    searchHistoryTypeId,
  }
}

export const useSearchState = () => {
  const {
    setAppliedSavedSearchName,
    appliedSavedSearchName,
    selectedSortId,
    setSelectedSortId,
    selectedGroupingId,
    setSelectedGroupingId,
    metaFilters,
    setMetaFilters,
    page,
    scrollX,
    scrollY,
    setPage,
    setScrollX,
    setScrollY,
    searchString,
    setSearchString,
    filtersSetup,
    setFiltersSetup,
    selectedFiltersByProfiles,
    setSelectedFiltersByProfiles,
    expandedFilter,
    setExpandedFilter,
    searchableProfilesMap,
    setSearchableProfilesMap,
    isSearchAllProfilesChecked,
    setSearchAllProfilesChecked,
    selectedMetaFilter,
    setSelectedMetaFilter,
    clear,
    savedSearchId,
    setSavedSearchId,
    setSearchHistoryTypeId,
    searchHistoryTypeId,
    eventsFilterData,
    setEventsFilter,
  } = useSearchQueryParams()
  const queryClient = useQueryClient()
  const [filterSectionOptionsSearch, setFilterSectionOptionsSearch] = useState('')
  const [filterSectionExpandAllOptions, setFilterSectionExpandAllOptions] = useState(false)

  const handleSetSearchableProfiles = useCallback(
    (entityName: ProfileType, value?: boolean) => {
      const enable = typeof value === 'boolean' ? value : !searchableProfilesMap[entityName]
      const newSearchableProfiles = { ...searchableProfilesMap, [entityName]: enable }
      const values = Object.values(newSearchableProfiles)
      const allTheSame = values.every(v => v === values[0])
      setAppliedSavedSearchName('')
      setMetaFilters(prevMetaFilters => ({ ...prevMetaFilters, filterByNewItems: false }))
      setSearchAllProfilesChecked(allTheSame)
      setSearchableProfilesMap((c: Record<string, boolean>) => ({ ...c, [entityName]: enable }))
      setPage(1)
      setScrollX(0)
      setScrollY(0)

      if (!newSearchableProfiles[ProfileType.company]) {
        setSelectedGroupingId(0)
      }
    },
    [searchableProfilesMap],
  )

  const processNewFilter = useCallback(
    (currentState: SelectedFiltersMapByProfiles, type: ProfileType, newList: { field: string; value: string }[]) => {
      setAppliedSavedSearchName('')
      setMetaFilters(prevMetaFilters => ({ ...prevMetaFilters, filterByNewItems: false }))
      if (!newList.length) {
        const newState = removeObjProperty(type, currentState) as SelectedFiltersMapByProfiles
        return newState
      }

      return {
        ...currentState,
        [type]: newList,
      }
    },
    [],
  )

  const handleSetAllSearchableProfiles = useCallback(
    (enable: boolean) => {
      setAppliedSavedSearchName('')
      setMetaFilters(prevMetaFilters => ({ ...prevMetaFilters, filterByNewItems: false }))
      setSearchAllProfilesChecked(enable)

      if (!enable) {
        setSearchableProfilesMap(getDefaultSearchableProfilesState(enable))
      } else {
        setSearchableProfilesMap(getDefaultSearchableProfilesState(false))
      }
      setPage(1)
      setScrollX(0)
      setScrollY(0)
    },
    [searchableProfilesMap, filtersSetup],
  )

  const handleSetNewFilters = useCallback(
    (filtersData: SelectedFiltersMapByProfiles | SetStateAction<SelectedFiltersMapByProfiles>) => {
      setAppliedSavedSearchName('')
      setMetaFilters(prevMetaFilters => ({ ...prevMetaFilters, filterByNewItems: false }))
      setPage(1)
      setScrollX(0)
      setScrollY(0)
      setSelectedFiltersByProfiles(filtersData)
    },
    [],
  )

  const handleClearSection = useCallback((type: ProfileType, field: string) => {
    handleSetNewFilters(currentState => {
      const newFilterByType = currentState[type]?.filter(section => section.field !== field)
      const newFilters = processNewFilter(currentState, type, newFilterByType)

      if (!newFilterByType.length) {
        setSearchableProfilesMap((c: Record<string, boolean>) => {
          const updatedProfilesState = { ...c, [type]: false }
          if (!Object.values(updatedProfilesState).find(Boolean)) {
            setSearchAllProfilesChecked(true)
          }

          return updatedProfilesState
        })
      }

      return newFilters
    })
  }, [])

  const addItemFilter = useCallback(
    (type: ProfileType, field: string, value: string, multiple: boolean) => {
      setAppliedSavedSearchName('')
      setMetaFilters(prevMetaFilters => ({ ...prevMetaFilters, filterByNewItems: false }))

      if (!multiple) {
        handleSetNewFilters(currentState => {
          let newList = currentState[type]?.filter(i => i.field !== field) || []

          if (value) {
            newList = [...newList, { field, value }]
          }

          return processNewFilter(currentState, type, newList)
        })
      } else {
        handleSetNewFilters(currentState => ({
          ...currentState,
          [type]: [...(currentState[type] || []), { field, value }],
        }))
      }

      const newSearchableProfiles = { ...searchableProfilesMap, [type]: true }
      const values = Object.values(newSearchableProfiles)
      const allTheSame = values.every(v => v === values[0])

      setSearchAllProfilesChecked(allTheSame)
      setSearchableProfilesMap((c: Record<string, boolean>) => ({ ...c, [type]: true }))

      setFiltersSetup(
        (currentFiltersSetup: FiltersSetup) =>
          Object.entries(currentFiltersSetup || {}).reduce<FiltersSetup>(
            (acc, [filter, data]) => ({ ...acc, [filter]: { ...data, enabled: type === filter } }),
            {},
          ) as FiltersSetup,
      )
    },
    [searchableProfilesMap, filtersSetup],
  )

  const handleSubmitMetaFilter = useCallback((metaFilterData: MetaFilterSelectedData) => {
    if (metaFilterData.type !== MetaFilterTypes.BY_NEW_ITEMS) {
      setAppliedSavedSearchName('')
    }

    setPage(1)
    setScrollX(0)
    setScrollY(0)
    setSelectedMetaFilter(metaFilterData)
  }, [])

  useEffect(() => {
    switch (selectedMetaFilter.type) {
      case MetaFilterTypes.BY_DATE_RANGE: {
        submitFilterByDate(selectedMetaFilter)
        break
      }
      case MetaFilterTypes.BY_EVENT: {
        setMetaFilters({ eventCode: selectedMetaFilter.value as number })
        break
      }
      case MetaFilterTypes.BY_RECENT: {
        submitFilterByRecent(selectedMetaFilter)
        break
      }
      case MetaFilterTypes.BY_NEW_ITEMS: {
        setMetaFilters({ filterByNewItems: true })
        break
      }
      default: {
        setMetaFilters({ filterByNewItems: false })
        break
      }
    }
  }, [selectedMetaFilter, setMetaFilters])

  const submitFilterByDate = useCallback((metaFilterData: MetaFilterSelectedData) => {
    const { from, to } = metaFilterData.value as MetaFilterSelectedDate

    const dateFrom = DateTime.fromFormat(from.toString(), 'yyyy').toJSDate().toISOString()
    const dateTo = DateTime.fromFormat(to.toString(), 'yyyy').endOf('year').toJSDate().toISOString()

    setMetaFilters({ dateFrom, dateTo } as SelectedMetaFilters)
  }, [])

  const submitFilterByRecent = useCallback((metaFilterData: MetaFilterSelectedData) => {
    let recentDateStart = DateTime.now().startOf('day').minus({ days: 7 }).toJSDate().toISOString()
    if (metaFilterData.value === 'SEARCH_FILTER_BY_RECENT_MONTH') {
      recentDateStart = DateTime.now().startOf('day').minus({ month: 1 }).toJSDate().toISOString()
    }
    if (metaFilterData.value === 'SEARCH_FILTER_BY_RECENT_THREE_MONTHS') {
      recentDateStart = DateTime.now().startOf('day').minus({ month: 3 }).toJSDate().toISOString()
    }
    setMetaFilters({ dateFrom: recentDateStart } as SelectedMetaFilters)
  }, [])

  const resetAll = useCallback(() => {
    setSearchString('')
    handleSetNewFilters({} as SelectedFiltersMapByProfiles)
    setPage(1)
    setScrollX(0)
    setScrollY(0)
    setSearchAllProfilesChecked(true)
    setSearchableProfilesMap(getDefaultSearchableProfilesState(false))
    setExpandedFilter({})
    setFilterSectionExpandAllOptions(false)
    setFilterSectionOptionsSearch('')
    setMetaFilters({})
    setSelectedSortId(DEFAULT_SORT_OPTION.id)
    setSelectedMetaFilter({ type: MetaFilterTypes.DEFAULT, value: '' })
    setSelectedGroupingId(0)
    setSearchHistoryTypeId(undefined)
    clear()
  }, [])

  const handleSearch = useCallback(
    (str: string) => {
      if (searchString === str) {
        return
      }
      setAppliedSavedSearchName('')
      setPage(1)
      setScrollX(0)
      setScrollY(0)
      setSearchString(str)
      queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key] })
    },
    [searchString],
  )

  const handleExpandFilter = useCallback((profileType: string, filterKey: string, expand: boolean, nestedName: string) => {
    if (!nestedName) {
      setFilterSectionExpandAllOptions(false)
      setFilterSectionOptionsSearch('')
    }

    setExpandedFilter((currentState: Record<string, Record<string, boolean | string>>) => {
      const currentExpandedSection = currentState[profileType]
      if (currentExpandedSection && nestedName) {
        const nestedExpandedFilter = expand ? nestedName : ''
        return {
          [profileType]: { ...currentExpandedSection, nestedExpandedFilter },
        }
      }
      if (currentExpandedSection && !expand) {
        return {}
      }
      return {
        [profileType]: { [filterKey]: expand },
      }
    })
  }, [])

  const handleSort = useCallback((id: number, withPageReset = true) => {
    setSelectedSortId(id)
    setAppliedSavedSearchName('')
    if (withPageReset) {
      setPage(1)
      setScrollX(0)
      setScrollY(0)
    }
  }, [])

  const handleGroupSelected = useCallback((id: number, withPageReset = true) => {
    setSelectedGroupingId(id)
    setSelectedSortId(DEFAULT_SORT_OPTION.id)
    setAppliedSavedSearchName('')
    if (withPageReset) {
      setPage(1)
      setScrollX(0)
      setScrollY(0)
    }
  }, [])

  const removeItemFilter = useCallback(
    (profileType: ProfileType, field: string, value: string) => {
      handleSetNewFilters(currentState => {
        const newFilterByType = currentState[profileType]?.filter(i => !(i.value === value && i.field === field))
        // @ts-expect-error temp
        setSearchableProfilesMap(c => {
          const newState = { ...c, [profileType]: newFilterByType?.length }
          if (!Object.values(newState).some(Boolean)) {
            setSearchAllProfilesChecked(true)
            return getDefaultSearchableProfilesState(false)
          }

          return newState
        })
        return processNewFilter(currentState, profileType, newFilterByType)
      })
    },
    [selectedFiltersByProfiles],
  )

  const toggleItemFilter = useCallback((type: ProfileType, field: string, value: string, direction: boolean, multiple = true) => {
    if (direction || !multiple) {
      addItemFilter(type, field, value, multiple)
    } else {
      removeItemFilter(type, field, value)
    }
  }, [])

  return {
    selectedSortId,
    selectedGroupingId,
    metaFilters,
    page,
    scrollX,
    scrollY,
    searchString,
    selectedMetaFilter,
    expandedFilter,
    filtersSetup,
    selectedFiltersByProfiles,
    isSearchAllProfilesChecked,
    searchableProfilesMap,
    filterSectionExpandAllOptions,
    filterSectionOptionsSearch,
    appliedSavedSearchName,
    setAppliedSavedSearchName,
    setPage,
    setScrollX,
    setScrollY,
    handleClearSection,
    setSearchString,
    handleSubmitMetaFilter,
    setFiltersSetup,
    resetAll,
    handleSearch,
    handleSort,
    handleGroupSelected,
    toggleItemFilter,
    handleExpandFilter,
    handleSetSearchableProfiles,
    handleSetAllSearchableProfiles,
    setSelectedFiltersByProfiles,
    setExpandedFilter,
    setSearchableProfilesMap,
    setSearchAllProfilesChecked,
    setFilterSectionExpandAllOptions,
    setFilterSectionOptionsSearch,
    savedSearchId,
    setSavedSearchId,
    searchHistoryTypeId,
    setSearchHistoryTypeId,
    eventsFilterData,
    setEventsFilter,
  }
}
